import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GrowthHealthResponse {
  status: string;
  moduleKey: string;
  defaultAutomationLevel: string;
  scope: string;
}

export interface GrowthTenantConfigResponse {
  tenantId: string;
  defaultIndustryKey: string;
  defaultAutomationLevel: string;
  allowedChannelsJson: string;
  isEnabled: boolean;
  guardrailPolicyJson: string;
}

export interface GrowthContextResponse {
  id: string;
  tenantId: string;
  partyId: string;
  customerAccountId: string | null;
  primaryIndustryKey: string | null;
  lifecycleStage: string;
  engagementScore: number | null;
  revenueScore: number | null;
  lastActivityAtUtc: string | null;
  sourceSummaryJson: string;
}

export interface GrowthIndustryPackResponse {
  id: string;
  key: string;
  displayName: string;
  version: string;
  installedVersion: string | null;
  lifecycleStatus: string;
  isInstalled: boolean;
  isSystem: boolean;
  catalogUpgradeAvailable: boolean;
  canInstall: boolean;
  kpiDefinitionsJson: string;
  segmentTemplatesJson: string;
  promptRefsJson: string;
  growthPlaybookJson: string;
  campaignTemplatesJson: string;
  dynamicFormDefinitionRefsJson: string;
  complianceNotesJson: string;
}

export interface IndustryBindingResponse {
  id: string;
  industryKey: string;
  entityType: string;
  entityId: string;
  partyId: string | null;
  growthContextId: string | null;
  metadataJson: string;
}

export interface GrowthAgentListingResponse {
  id: string;
  key: string;
  displayName: string;
  version: string;
  agentDefinitionKey: string;
  industryKeysJson: string;
  permissionManifestJson: string;
  publisherType: string;
  description: string | null;
  isCurated: boolean;
  isPublished: boolean;
  canInstall: boolean;
  installLifecycleStatus: string | null;
  installId: string | null;
  killSwitchEnabled: boolean;
}

export interface GrowthAgentInstallResponse {
  id: string;
  listingKey: string;
  agentDefinitionKey: string;
  displayName: string;
  version: string;
  installedVersion: string | null;
  industryPackKey: string | null;
  permissionManifestJson: string;
  lifecycleStatus: string;
  killSwitchEnabled: boolean;
  killSwitchReason: string | null;
}

export interface GrowthConnectorDto {
  id: string;
  connectorKey: string;
  displayName: string;
  direction: string;
  kind: string;
  providerKey: string;
  authConfigRef: string;
  mappingJson: string;
  webhookSecretMasked: string | null;
  status: string;
  scheduleMode: string;
  isSystemSeed: boolean;
}

export interface GrowthConnectorSyncRunDto {
  id: string;
  connectorId: string;
  connectorKey: string;
  direction: string;
  status: string;
  startedAtUtc: string;
  finishedAtUtc: string | null;
  summaryJson: string;
  error: string | null;
  leadId: string | null;
  providerEventId: string | null;
}

@Injectable({ providedIn: 'root' })
export class GrowthAdminService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  getHealth(): Observable<GrowthHealthResponse> {
    return this.http.get<GrowthHealthResponse>(`${this.apiBaseUrl}/api/v1/growth/health`);
  }

  getTenantConfig(): Observable<GrowthTenantConfigResponse> {
    return this.http.get<GrowthTenantConfigResponse>(`${this.apiBaseUrl}/api/v1/growth/admin/tenant-config`);
  }

  updateTenantConfig(payload: {
    defaultIndustryKey: string;
    defaultAutomationLevel: string;
    allowedChannelsJson: string;
    isEnabled: boolean;
    guardrailPolicyJson: string;
  }): Observable<GrowthTenantConfigResponse> {
    return this.http.put<GrowthTenantConfigResponse>(`${this.apiBaseUrl}/api/v1/growth/admin/tenant-config`, payload);
  }

  getContextByParty(partyId: string): Observable<GrowthContextResponse> {
    return this.http.get<GrowthContextResponse>(`${this.apiBaseUrl}/api/v1/growth/context/${partyId}`);
  }

  listIndustryPacks(): Observable<GrowthIndustryPackResponse[]> {
    return this.http.get<GrowthIndustryPackResponse[]>(`${this.apiBaseUrl}/api/v1/growth/industry/packs`);
  }

  getIndustryPack(key: string): Observable<GrowthIndustryPackResponse> {
    return this.http.get<GrowthIndustryPackResponse>(`${this.apiBaseUrl}/api/v1/growth/industry/packs/${encodeURIComponent(key)}`);
  }

  installIndustryPack(key: string): Observable<GrowthIndustryPackResponse> {
    return this.http.post<GrowthIndustryPackResponse>(`${this.apiBaseUrl}/api/v1/growth/industry/packs/${encodeURIComponent(key)}/install`, null);
  }

  disableIndustryPack(key: string): Observable<GrowthIndustryPackResponse> {
    return this.http.post<GrowthIndustryPackResponse>(`${this.apiBaseUrl}/api/v1/growth/industry/packs/${encodeURIComponent(key)}/disable`, null);
  }

  upgradeIndustryPack(key: string): Observable<GrowthIndustryPackResponse> {
    return this.http.post<GrowthIndustryPackResponse>(`${this.apiBaseUrl}/api/v1/growth/industry/packs/${encodeURIComponent(key)}/upgrade`, null);
  }

  getBinding(id: string): Observable<IndustryBindingResponse> {
    return this.http.get<IndustryBindingResponse>(`${this.apiBaseUrl}/api/v1/growth/industry/bindings/${id}`);
  }

  createBinding(payload: {
    industryKey: string;
    entityType: string;
    entityId: string;
    partyId?: string | null;
    growthContextId?: string | null;
    metadataJson?: string | null;
  }): Observable<IndustryBindingResponse> {
    return this.http.post<IndustryBindingResponse>(`${this.apiBaseUrl}/api/v1/growth/industry/bindings`, payload);
  }

  listBindings(filter?: { industryKey?: string; entityType?: string; entityId?: string }): Observable<IndustryBindingResponse[]> {
    let params = new HttpParams();
    if (filter?.industryKey) params = params.set('industryKey', filter.industryKey);
    if (filter?.entityType) params = params.set('entityType', filter.entityType);
    if (filter?.entityId) params = params.set('entityId', filter.entityId);
    return this.http.get<IndustryBindingResponse[]>(`${this.apiBaseUrl}/api/v1/growth/industry/bindings`, { params });
  }

  listAgentMarketplaceListings(curatedOnly = true): Observable<GrowthAgentListingResponse[]> {
    const params = new HttpParams().set('curatedOnly', curatedOnly);
    return this.http.get<GrowthAgentListingResponse[]>(`${this.apiBaseUrl}/api/v1/growth/marketplace/listings`, { params });
  }

  getAgentMarketplaceListing(key: string): Observable<GrowthAgentListingResponse> {
    return this.http.get<GrowthAgentListingResponse>(`${this.apiBaseUrl}/api/v1/growth/marketplace/listings/${encodeURIComponent(key)}`);
  }

  installAgentMarketplaceListing(key: string, industryPackKey?: string | null): Observable<GrowthAgentInstallResponse> {
    return this.http.post<GrowthAgentInstallResponse>(
      `${this.apiBaseUrl}/api/v1/growth/marketplace/listings/${encodeURIComponent(key)}/install`,
      industryPackKey ? { industryPackKey } : {}
    );
  }

  listAgentMarketplaceInstalls(): Observable<GrowthAgentInstallResponse[]> {
    return this.http.get<GrowthAgentInstallResponse[]>(`${this.apiBaseUrl}/api/v1/growth/marketplace/installs`);
  }

  getAgentMarketplaceInstall(id: string): Observable<GrowthAgentInstallResponse> {
    return this.http.get<GrowthAgentInstallResponse>(`${this.apiBaseUrl}/api/v1/growth/marketplace/installs/${id}`);
  }

  disableAgentMarketplaceInstall(id: string): Observable<GrowthAgentInstallResponse> {
    return this.http.post<GrowthAgentInstallResponse>(`${this.apiBaseUrl}/api/v1/growth/marketplace/installs/${id}/disable`, null);
  }

  updateAgentMarketplaceInstallPermissions(id: string, permissionManifestJson: string): Observable<GrowthAgentInstallResponse> {
    return this.http.put<GrowthAgentInstallResponse>(
      `${this.apiBaseUrl}/api/v1/growth/marketplace/installs/${id}/permissions`,
      { permissionManifestJson }
    );
  }

  setAgentMarketplaceInstallKillSwitch(id: string, enabled: boolean, reason?: string | null): Observable<GrowthAgentInstallResponse> {
    return this.http.post<GrowthAgentInstallResponse>(
      `${this.apiBaseUrl}/api/v1/growth/marketplace/installs/${id}/kill-switch`,
      { enabled, reason: reason ?? null }
    );
  }

  listGrowthConnectors(): Observable<GrowthConnectorDto[]> {
    return this.http.get<GrowthConnectorDto[]>(`${this.apiBaseUrl}/api/v1/growth/integration/connectors`);
  }

  getGrowthConnector(key: string): Observable<GrowthConnectorDto> {
    return this.http.get<GrowthConnectorDto>(`${this.apiBaseUrl}/api/v1/growth/integration/connectors/${encodeURIComponent(key)}`);
  }

  upsertGrowthConnector(payload: {
    connectorKey: string;
    displayName: string;
    direction: string;
    kind: string;
    providerKey: string;
    authConfigRef?: string | null;
    mappingJson?: string | null;
    webhookSecret?: string | null;
    status?: string | null;
    scheduleMode?: string | null;
  }): Observable<GrowthConnectorDto> {
    return this.http.post<GrowthConnectorDto>(`${this.apiBaseUrl}/api/v1/growth/integration/connectors`, payload);
  }

  disableGrowthConnector(key: string): Observable<GrowthConnectorDto> {
    return this.http.post<GrowthConnectorDto>(
      `${this.apiBaseUrl}/api/v1/growth/integration/connectors/${encodeURIComponent(key)}/disable`,
      null
    );
  }

  triggerGrowthConnectorOutboundSync(key: string): Observable<GrowthConnectorSyncRunDto> {
    return this.http.post<GrowthConnectorSyncRunDto>(
      `${this.apiBaseUrl}/api/v1/growth/integration/connectors/${encodeURIComponent(key)}/outbound-sync`,
      null
    );
  }

  listGrowthConnectorSyncStatus(connectorKey?: string | null): Observable<GrowthConnectorSyncRunDto[]> {
    let params = new HttpParams();
    if (connectorKey) {
      params = params.set('connectorKey', connectorKey);
    }
    return this.http.get<GrowthConnectorSyncRunDto[]>(`${this.apiBaseUrl}/api/v1/growth/integration/sync-status`, { params });
  }
}
